/**
 * Unified Order Service - Ensures consistent order data across all components
 * 
 * This service provides a single source of truth for order data,
 * preventing inconsistencies between different API endpoints.
 */

import { useAppSelector } from '@/redux/hooks';
import axiosInstance from './axiosInstance';
import { getAccessToken } from './tokenService';
// import { debugDataConsistency } from '../utils/dataUtils';
// import { 
//   mapBackendOrdersToUnified, 
//   mapBackendOrderToUnified, 
//   mapUnifiedToLegacyArray,
//   // debugDataMapping,
//   validateMappedOrder,
//   BackendOrder,
//   anyData
// } from '../utils/dataMapping';

// Using anyData from dataMapping instead of defining our own
// export type any = anyData;

export interface OrderStats {
  totalOrders: number;
  todayOrders: number;
  pendingOrders: number;
  inTransitOrders: number;
  deliveredOrders: number;
  failedOrders: number;
  totalRevenue: number;
  pickupRequested: number;
}


/**
 * Primary Orders API - Single source of truth for all order data
 */
class OrderService {

  private getBaseEndpoint(): string {
    // Get user role from token or localStorage
    const token = getAccessToken();
    if (!token) return '/orders'; // Default to pharmacy
    
    try {
      // Decode JWT token to get user role
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log("PAYLOAD", payload);
      console.log(payload.isAdmin ? '/admin/orders' : '/orders');
      return payload.isAdmin ? '/admin/orders' : '/orders';
    } catch (error) {
      console.warn('Failed to decode token, defaulting to pharmacy endpoint');
      return '/orders';
    }
  }
  // private baseEndpoint = this.getBaseEndpoint();
  private get baseEndpoint(): string {
    return this.getBaseEndpoint();
  }

  /**
   * Get all orders with optional filtering
   * Now compatible with backend unified field handling (pharmacy/pharmacyId)
   */
  async getAllOrders(filters?: {
    status?: string;
    priority?: string;
    dateRange?: string;
    pharmacyId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ orders: any[]; stats: OrderStats; pagination?: { total: number; page: number; limit: number; totalPages: number } }> {
    try {
      // console.log('📋 OrderService: Fetching all orders with unified backend...');
      
      // Build query parameters - only include supported parameters
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.priority) params.append('priority', filters.priority);
      // Note: dateRange parameter removed as backend doesn't support it
      if (filters?.pharmacyId) params.append('pharmacyId', filters.pharmacyId);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString();
      const endpoint = queryString ? `${this.baseEndpoint}?${queryString}` : this.baseEndpoint;
      
      const response = await axiosInstance.get(endpoint);
      // debugDataConsistency('OrderService - getAllOrders (Unified Backend)', response.data);

      // Extract raw backend orders
      const orders: [] = Array.isArray(response.data.data) ? response.data.data : response.data.data?.orders || [];
      // console.log('📥 Raw backend orders:', rawOrders.length);
      
      // Map to unified format
      // const anys = mapBackendOrdersToUnified(rawOrders);
      // console.log('🔄 Mapped to unified orders:', anys.length);
      
      // Debug mapping issues
      // debugDataMapping('OrderService - getAllOrders', rawOrders, anys);
      
      // Validate mapped orders
      // const validationResults = anys.map(order => validateMappedOrder(order));
      // const invalidOrders = validationResults.filter(result => !result.isValid);
      // if (invalidOrders.length > 0) {
      //   // console.warn('⚠️ Some orders have mapping issues:', invalidOrders);
      // }
      
      const stats = this.calculateStats(orders);

      // Extract pagination info if backend provides it
      const pagination = response.data.pagination ? {
        total: response.data.pagination.total || orders.length,
        page: response.data.pagination.page || filters?.page || 1,
        limit: response.data.pagination.limit || filters?.limit || orders.length,
        totalPages: response.data.pagination.pages || response.data.pagination.totalPages || Math.ceil(orders.length / (filters?.limit || orders.length))
      } : undefined;

      // console.log('✅ OrderService: Orders fetched and mapped successfully', {
      //   rawCount: rawOrders.length,
      //   unifiedCount: anys.length,
      //   todayCount: stats.todayOrders,
      //   invalidCount: invalidOrders.length,
      //   endpoint: endpoint
      // });

      return { orders, stats, pagination };
    } catch (error: any) {
      console.error('❌ OrderService: Error fetching orders:', error);
      
      // Handle 400 Bad Request specifically
      if (error.response?.status === 400) {
        // console.warn('⚠️ Backend returned 400 - likely unsupported query parameters');
        // console.log('🔄 Retrying without query parameters...');
        
        try {
          // Retry without any query parameters
          const response = await axiosInstance.get(this.baseEndpoint);
          const orders: any[] = Array.isArray(response.data.data) ? response.data.data : response.data.data?.orders || [];
          const stats = this.calculateStats(orders);
          
          // console.log('✅ OrderService: Retry successful without query parameters', {
          //   count: anys.length
          // });
          
          return { orders, stats };
        } catch (retryError: any) {
          console.error('❌ OrderService: Retry also failed:', retryError);
          throw new Error(`Failed to fetch orders: ${retryError.response?.data?.message || retryError.message}`);
        }
      }
      
      throw new Error(`Failed to fetch orders: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get today's orders specifically
   */
  async getTodaysOrders(): Promise<{ orders: any[]; count: number }> {
    const today = new Date().toISOString().split('T')[0];
    
    try {
      // Get all orders and filter by today's date on frontend
      // since backend doesn't support dateRange parameter
      const { orders } = await this.getAllOrders();
      
      // Filter by today's date
      const todayOrders = orders.filter(order => {
        const orderDate = new Date(order.deliveryDate || order.createdAt).toISOString().split('T')[0];
        return orderDate === today;
      });

      // console.log('📅 OrderService: Today orders fetched', { 
      //   totalOrders: orders.length,
      //   todayOrders: todayOrders.length, 
      //   date: today 
      // });
      
      return { orders: todayOrders, count: todayOrders.length };
    } catch (error: any) {
      console.error('❌ OrderService: Error fetching today orders:', error);
      return { orders: [], count: 0 };
    }
  }

  /**
   * Get orders for delivery tracking
   */
  async getDeliveryOrders(): Promise<{ orders: any[]; stats: OrderStats }> {
    try {
      // console.log('🚚 OrderService: Fetching delivery orders...');
      
      // Get all orders for delivery tracking
      const { orders, stats } = await this.getAllOrders();
      
      // Filter orders relevant for delivery (not just pending creation)
      const deliveryOrders = orders.filter(order => 
        order.status !== 'draft' && order.deliveryStatus !== 'not-ready'
      );

      // console.log('✅ OrderService: Delivery orders filtered', { 
      //   total: orders.length, 
      //   delivery: deliveryOrders.length 
      // });

      return { 
        orders: deliveryOrders, 
        stats: this.calculateStats(deliveryOrders) 
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error fetching delivery orders:', error);
      // Fallback to getAllOrders if delivery-specific logic fails
      return await this.getAllOrders();
    }
  }

  /**
   * Create a new order
   */
  async createOrder(orderData:any): Promise<{ order: any; success: boolean }> {
    try {
      console.log('➕ OrderService: Creating order...', orderData);
      
      const response = await axiosInstance.post(this.baseEndpoint, orderData);
      // debugDataConsistency('OrderService - createOrder response', response.data);

      // Map backend response to unified format
      // const rawOrder: BackendOrder = response.data.data;
      const order = response.data.data;
      // const any = mapBackendOrderToUnified(rawOrder);
      
      // Debug mapping
      // debugDataMapping('OrderService - createOrder', rawOrder, any);
      
      // Validate mapped order
      // const validation = validateMappedOrder(any);
      // if (!validation.isValid) {
      //   // console.warn('⚠️ Created order has mapping issues:', validation.issues);
      // }
      
      // console.log('✅ OrderService: Order created and mapped successfully', { 
      //   orderNumber: any.orderNumber,
      //   validationIssues: validation.issues
      // });

      return { order, success: true };
    } catch (error: any) {
      console.error('❌ OrderService: Error creating order:', error);
      throw new Error(`Failed to create order: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Update order status
   */
  async updateOrderStatus(orderId: string, status: string, additionalData?: any): Promise<any> {
    try {
      // console.log('🔄 OrderService: Updating order status...', { orderId, status });
      
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${orderId}/status`, {
        status,
        ...additionalData
      });

      const order = response.data.data;
      // console.log('✅ OrderService: Order status updated', { orderNumber: order.orderNumber, status });

      return order;
    } catch (error: any) {
      console.error('❌ OrderService: Error updating order status:', error);
      throw new Error(`Failed to update order status: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get all orders for admin processing with advanced filtering
   */
  async getAllOrdersForAdmin(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    pharmacy?: string;
    dateRange?: string;
    excludeStatus?: string;
  }): Promise<{ success: boolean; data: { orders: any[]; pagination: any } }> {
    try {
      console.log('📋 OrderService: Fetching all orders for admin processing...');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.pharmacy) params.append('pharmacy', filters.pharmacy);
      if (filters?.dateRange) params.append('dateRange', filters.dateRange);
      if (filters?.excludeStatus) params.append('excludeStatus', filters.excludeStatus);

      const queryString = params.toString();
      const endpoint = queryString ? `${this.baseEndpoint}/admin?${queryString}` : `${this.baseEndpoint}/admin`;
      
      const response = await axiosInstance.get(endpoint);
      
      return {
        success: true,
        data: {
          orders: response.data.data?.orders || response.data.data || [],
          pagination: response.data.pagination || {
            total: response.data.data?.length || 0,
            page: filters?.page || 1,
            pages: 1,
            limit: filters?.limit || 10
          }
        }
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error fetching admin orders:', error);
      
      // Fallback to regular getAllOrders if admin endpoint doesn't exist
      try {
        const { orders } = await this.getAllOrders();
        
        // Apply client-side filtering
        let filteredOrders = orders;
        
        if (filters?.excludeStatus) {
          filteredOrders = filteredOrders.filter(order => order.status !== filters.excludeStatus);
        }
        if (filters?.status && filters.status !== 'all') {
          filteredOrders = filteredOrders.filter(order => order.status === filters.status);
        }
        if (filters?.search) {
          const searchLower = filters.search.toLowerCase();
          filteredOrders = filteredOrders.filter(order => 
            order.orderNumber?.toLowerCase().includes(searchLower) ||
            order.customerName?.toLowerCase().includes(searchLower) ||
            order.pharmacyName?.toLowerCase().includes(searchLower)
          );
        }
        if (filters?.pharmacy && filters.pharmacy !== 'all') {
          filteredOrders = filteredOrders.filter(order => order.pharmacyName === filters.pharmacy);
        }
        
        // Apply pagination
        const page = filters?.page || 1;
        const limit = filters?.limit || 10;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedOrders = filteredOrders.slice(startIndex, endIndex);
        
        return {
          success: true,
          data: {
            orders: paginatedOrders,
            pagination: {
              total: filteredOrders.length,
              page: page,
              pages: Math.ceil(filteredOrders.length / limit),
              limit: limit
            }
          }
        };
      } catch (fallbackError) {
        console.error('❌ OrderService: Fallback also failed:', fallbackError);
        return {
          success: false,
          data: { orders: [], pagination: { total: 0, page: 1, pages: 1, limit: 10 } }
        };
      }
    }
  }

  /**
   * Get single order by ID for admin (admin function)
   */
  async getOrderByIdForAdmin(orderId: string): Promise<{ success: boolean; data?: any }> {
    try {
      console.log('🔍 OrderService: Fetching order by ID for admin...', { orderId });
      
      const response = await axiosInstance.get(`${this.baseEndpoint}/${orderId}`);
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error fetching order by ID:', error);
      throw new Error(`Failed to fetch order: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Update order details (admin function)
   */
  async updateOrder(orderId: string, updateData: any): Promise<{ success: boolean; data?: any }> {
    try {
      console.log('🔄 OrderService: Updating order details...', { orderId, updateData });
      
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${orderId}`, updateData);
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error updating order:', error);
      return {
        success: false
      };
    }
  }

  /**
   * Delete order (admin function)
   */
  async deleteOrder(orderId: string): Promise<{ success: boolean }> {
    try {
      console.log('🗑️ OrderService: Deleting order...', { orderId });
      
      await axiosInstance.delete(`${this.baseEndpoint}/${orderId}`);
      
      return { success: true };
    } catch (error: any) {
      console.error('❌ OrderService: Error deleting order:', error);
      return { success: false };
    }
  }

  /**
   * Generate signature link for customer (admin function)
   */
  async generateSignatureLink(orderId: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      console.log('📝 OrderService: Generating signature link...', { orderId });
      
      const response = await axiosInstance.post(`/admin/orders/${orderId}/generate-signature-link`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error generating signature link:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate signature link'
      };
    }
  }

  /**
   * Regenerate documents with customer signature (admin function)
   */
  async regenerateDocuments(orderId: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      console.log('📄 OrderService: Regenerating documents...', { orderId });
      
      const response = await axiosInstance.post(`/admin/orders/${orderId}/regenerate-documents`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error regenerating documents:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to regenerate documents'
      };
    }
  }

  /**
   * Get signature status for an order (admin function)
   */
  async getSignatureStatus(orderId: string): Promise<{ success: boolean; data?: any }> {
    try {
      const response = await axiosInstance.get(`/admin/orders/${orderId}/signature-status`);
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error getting signature status:', error);
      return {
        success: false
      };
    }
  }

  /**
   * Submit customer signature (public function - no auth)
   */
  async submitSignature(token: string, signatureImage: string): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      console.log('✍️ OrderService: Submitting signature...');
      
      const response = await axiosInstance.post(`/signature/${token}`, {
        signatureImage
      });
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error submitting signature:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to submit signature'
      };
    }
  }

  /**
   * Get analytics data (unified with order stats)
   */
  async getAnalyticsData(timeRange: string = '7d'): Promise<{
    basicStats: OrderStats;
    recentOrders: any[];
    deliveryInsights: any[];
    medicationCategories: any[];
  }> {
    try {
      // console.log('📊 OrderService: Fetching analytics data...');
      
      // Get orders without dateRange parameter to avoid 400 error
      // The backend will handle time filtering internally
      const { orders, stats } = await this.getAllOrders();
      
      // Get recent orders (last 10)
      const recentOrders = orders
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);

      // Calculate delivery insights from orders
      const deliveryInsights = this.calculateDeliveryInsights(orders);
      
      // Calculate medication categories from orders
      const medicationCategories = this.calculateMedicationCategories(orders);

      // console.log('✅ OrderService: Analytics data calculated', {
      //   ordersCount: orders.length,
      //   recentCount: recentOrders.length,
      //   insights: deliveryInsights.length
      // });

      return {
        basicStats: stats,
        recentOrders,
        deliveryInsights,
        medicationCategories
      };
    } catch (error: any) {
      console.error('❌ OrderService: Error fetching analytics:', error);
      // Return empty data structure to prevent crashes
      return {
        basicStats: this.getEmptyStats(),
        recentOrders: [],
        deliveryInsights: [],
        medicationCategories: []
      };
    }
  }

  /**
   * Calculate order statistics from order array
   */
  private calculateStats(orders: any[]): any {
    const today = new Date().toISOString().split('T')[0];
    
    return {
      totalOrders: orders.length,
      todayOrders: orders.filter(order => {
        const orderDate = new Date(order.deliveryDate || order.createdAt).toISOString().split('T')[0];
        return orderDate === today;
      }).length,
      pendingOrders: orders.filter(order => 
        ['pending', 'created', 'confirmed'].includes((order.pickupStatus || order.status || '').toLowerCase())
      ).length,
      pickupRequested: orders.filter(order => 
        (order.pickupStatus || order.status || '').toLowerCase() === 'pickup-requested'
      ).length,
      assignedOrders: orders.filter(order => 
        (order.pickupStatus || order.status || '').toLowerCase() === 'assigned'
      ).length,
      inTransitOrders: orders.filter(order => {
        const status = (order.pickupStatus || order.status || '').toLowerCase();
        return ['out-for-delivery', 'picked-up', 'in-route'].includes(status.replace(/\s+/g, '-'));
      }).length,
      deliveredOrders: orders.filter(order => 
        (order.deliveryStatus || order.status || '').toLowerCase() === 'delivered'
      ).length,
      failedOrders: orders.filter(order => {
        const status = (order.deliveryStatus || order.status || '').toLowerCase();
        return ['failed', 'cancelled', 'returned'].includes(status);
      }).length,
      totalRevenue: orders.reduce((sum, order) => sum + (order.totalValue || 0), 0)
    };
  }

  /**
   * Calculate delivery insights from orders
   */
  private calculateDeliveryInsights(orders: any[]): any[] {
    // Group orders by date for last 7 days
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last7Days.map(date => {
      const dayOrders = orders.filter(order => {
        const orderDate = new Date(order.deliveryDate || order.createdAt).toISOString().split('T')[0];
        return orderDate === date;
      });

      const delivered = dayOrders.filter(o => o.status?.toLowerCase() === 'delivered').length;
      const total = dayOrders.length;
      
      return {
        day: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
        date,
        deliveries: total,
        successRate: total > 0 ? Math.round((delivered / total) * 100) : 0,
        avgTime: 45 // Estimated average delivery time
      };
    });
  }

  /**
   * Calculate medication categories from orders
   */
  private calculateMedicationCategories(orders: any[]): any[] {
    const categoryMap = new Map();

    orders.forEach(order => {
      order.medicines?.forEach(medicine => {
        const category = medicine.name.split(' ')[0]; // Simple categorization
        const current = categoryMap.get(category) || { name: category, count: 0, revenue: 0 };
        current.count += medicine.quantity;
        current.revenue += medicine.subTotal;
        categoryMap.set(category, current);
      });
    });

    return Array.from(categoryMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Get empty stats structure
   */
  private getEmptyStats(): OrderStats {
    return {
      totalOrders: 0,
      todayOrders: 0,
      pendingOrders: 0,
      inTransitOrders: 0,
      deliveredOrders: 0,
      failedOrders: 0,
      totalRevenue: 0,
      pickupRequested: 0
    };
  }
}

// Export singleton instance
export const orderService = new OrderService();

// Export convenience functions
export const getAllOrders = (filters?: any) => orderService.getAllOrders(filters);
export const getTodaysOrders = () => orderService.getTodaysOrders();
export const getDeliveryOrders = () => orderService.getDeliveryOrders();
export const createOrder = (orderData: any) => orderService.createOrder(orderData);
export const updateOrderStatus = (orderId: string, status: string, additionalData?: any) => 
  orderService.updateOrderStatus(orderId, status, additionalData);
export const getAnalyticsData = (timeRange?: string) => orderService.getAnalyticsData(timeRange);
export const getOrderByIdForAdmin = (orderId: string) => orderService.getOrderByIdForAdmin(orderId);
